<template>
  <DialogLayout
    ref="assignLessonDialog"
    disableCloseHide
    :title="title"
    :enableConfirm="enableConfirm"
    :enableCancel="enableCancel"
    :cancelBtnText="cancelBtnText"
    btnAlign="space-between"
    cardContentClass="pa-0"
    @close="_close($event)"
  >
    <div class="pa-4">
      <v-radio-group
        v-if="enableLessonType"
        name="assignLessonType"
        v-model="filter.lesson_type"
        :readonly="loading"
        hide-details
        row
        @change="_onFilterChange('filterLessonType', 'lesson_type', $event)"
        class="ma-0"
      >
        <v-radio :label="$t('applyCourse')" value="weekday"></v-radio>
        <v-radio :label="$t('applySingleLesson')" value="singleDate"></v-radio>
      </v-radio-group>

      <v-row class="ma-0 my-4">
        <v-col cols="12" sm="6">
          <FormSearchSelect
            ref="searchCourseRef"
            label="searchCourse"
            placeholder="selectCourseName"
            :fieldValue.sync="filter.course_name"
            :options="courseResultOptions"
            searchPlaceholder="searchCourse"
            :searching="courseSearching"
            :readonly="loading"
            hideDetails
            @click-search="_searchCourse($event)"
            @changed="_onFilterChange('filterCourseName', 'course_name', $event)"
            enableClear
          />
        </v-col>

        <v-col cols="12" sm="6">
          <FormDatePicker
            label="lessonDate"
            placeholder="selectLessonDate"
            :dateValue.sync="filter.dates"
            isDateRange
            :readonly="loading"
            hideDetails
            @changed="_onFilterChange('filterDates', 'dates', $event)"
          />
        </v-col>

        <v-col cols="12" sm="6" v-if="filter.lesson_type === 'weekday'">
          <FormSelect
            label="weekday"
            placeholder="selectWeekday"
            :fieldValue.sync="filter.weekdays"
            :options="weekdayOptions"
            :readonly="loading"
            hideDetails
            isMultiple
            @changed="_onFilterChange('filterWeekdays', 'weekdays', $event)"
          />
        </v-col>

        <v-col cols="12" sm="6">
          <FormSelect
            label="tutor"
            placeholder="selectTutor"
            :fieldValue.sync="filter.tutor"
            :options="tutorOptions"
            hideDetails
            :readonly="loading"
            @changed="_onFilterChange('filterTutor', 'tutor', $event)"
            enableClear
          />
        </v-col>

        <v-col cols="12" sm="6" v-if="!disableGrade">
          <FormSelect
            label="grade"
            placeholder="selectGrade"
            :fieldValue.sync="filter.grade"
            :options="$store.getters.gradeOptions"
            hideDetails
            :readonly="loading"
            @changed="_onFilterChange('filterGrade', 'grade', $event)"
            enableClear
          />
        </v-col>
      </v-row>
    </div>

    <slot name="extra"></slot>

    <Datatable
      v-show="showTable"
      :isLoading="loading"
      :tableData="tableData"
      :tableHeaders="tableHeaders"
      :itemTotal="tableTotal"
      :page.sync="filter.page"
      :pageLimit.sync="filter.limit"
      :pageLimitOptions="[5, 10, 20]"
      @options-update="_onTableOptionsChange($event)"
      tableEmptyText="noCourse"
    >
      <template v-slot:[`item.actionSelectCourse`]="{ item }">
        <slot name="actionSelectCourse" :item="item"></slot>
      </template>
    </Datatable>

    <v-list v-if="tempSelectedLesson.length > 0" class="px-4 my-4">
      {{ $t('selectedLessons') }} ({{ tempSelectedLesson.length }}) :
      <v-list-item
        class="my-1 pa-0"
        v-for="(item, i) in tempSelectedLesson"
        style="min-height: unset;"
        :key="`temp_selected_lesson_${i}`"
      >
        {{ item.course_name }} {{ item.date.substring(8, 10) }}/{{ item.date.substring(5, 7) }}/{{
          item.date.substring(0, 4)
        }}
        ({{ $d($formatter.convertDate(item.date), 'weekdayShort') }}) {{ item.start_time }}-{{ item.end_time }}
      </v-list-item>
    </v-list>
  </DialogLayout>
</template>

<script>
import DialogLayout from '@/components/layout/DialogLayout.vue'
import FormSelect from '@/components/formField/FormSelect.vue'
import FormDatePicker from '@/components/formField/FormDatePicker.vue'
import Datatable from '@/components/Datatable.vue'
import FormSearchSelect from '@/components/formField/FormSearchSelect.vue'

export default {
  name: 'AssignLessonDialog',
  components: {
    DialogLayout,
    FormSelect,
    FormDatePicker,
    Datatable,
    FormSearchSelect,
  },
  props: {
    title: {
      type: String,
      default: 'applyNewCourse',
      required: false,
    },
    loading: {
      type: Boolean,
      default: false,
      required: false,
    },
    enableConfirm: {
      type: Boolean,
      default: true,
      required: false,
    },
    enableCancel: {
      type: Boolean,
      default: false,
      required: false,
    },
    cancelBtnText: {
      type: String,
      default: 'cancel',
      required: false,
    },
    enableLessonType: {
      type: Boolean,
      default: false,
      required: false,
    },
    courseNameOptions: {
      type: Array,
      default: () => [],
      required: false,
    },
    courseNameSearching: {
      type: Boolean,
      default: false,
      required: false,
    },
    tutorOptions: {
      type: Array,
      default: () => [],
      required: false,
    },
    disableGrade: {
      type: Boolean,
      default: false,
      required: false,
    },
    showTable: {
      type: Boolean,
      default: true,
      required: false,
    },
    tableData: {
      type: Array,
      default: () => [],
      required: true,
    },
    tableHeaders: {
      type: Array,
      default: () => [],
      required: true,
    },
    tableTotal: {
      type: Number,
      default: 0,
      required: true,
    },
    tempSelectedLesson: {
      type: Array,
      default: () => [],
      required: false,
    },
    // filter value
    filterLessonType: {
      type: String,
      default: 'weekday',
      required: false,
    },
    filterCourseName: {
      type: String,
      default: '',
      required: false,
    },
    filterDates: {
      type: Array,
      default: () => [],
      required: false,
    },
    filterWeekdays: {
      type: Array,
      default: () => [],
      required: false,
    },
    filterTutor: {
      type: Number | null,
      default: null,
      required: false,
    },
    filterGrade: {
      type: String,
      default: '',
      required: false,
    },
    filterPage: {
      type: Number,
      default: 1,
      required: true,
    },
    filterLimit: {
      type: Number,
      default: 5,
      required: true,
    },
    filterCenter: {
      type: Number,
      default: -1,
      required: false,
    },
  },
  watch: {
    filterLessonType(val) {
      this.filter.lesson_type = val
    },
    filterCourseName(val) {
      this.filter.course_name = val
    },
    filterDates(val) {
      this.filter.dates = val
    },
    filterWeekdays(val) {
      this.filter.weekdays = val
    },
    filterTutor(val) {
      this.filter.tutor = val
    },
    filterGrade(val) {
      this.filter.grade = val
    },
    filterPage(val) {
      this.filter.page = val
    },
    filterLimit: {
      handler(val) {
        this.filter.limit = val
      },
      immediate: true,
    },
    courseNameSearching(val) {
      this.courseSearching = val
    },
    courseNameOptions(val) {
      if (this.filter.lesson_type === 'weekday') {
        this.courseResultOptions = val.filter(el => el.lesson_type.includes('regular'))
      } else {
        this.courseResultOptions = val
      }
    },
  },
  data: vm => ({
    filter: {
      lesson_type: 'weekday',
      course_name: '',
      dates: [],
      weekdays: [],
      tutor: null,
      grade: '',
      page: 1,
      limit: 5,
    },
    courseSearching: false,

    courseResultOptions: [],

    weekdayOptions: [
      { value: '0', text: vm.$t('weekdaysShort[0]') },
      { value: '1', text: vm.$t('weekdaysShort[1]') },
      { value: '2', text: vm.$t('weekdaysShort[2]') },
      { value: '3', text: vm.$t('weekdaysShort[3]') },
      { value: '4', text: vm.$t('weekdaysShort[4]') },
      { value: '5', text: vm.$t('weekdaysShort[5]') },
      { value: '6', text: vm.$t('weekdaysShort[6]') },
    ],
  }),
  methods: {
    show(name = '') {
      this.$refs.assignLessonDialog.show(name)
    },
    hide(abort = false) {
      this.$refs.assignLessonDialog.hide(abort)
    },
    isOpen() {
      return this.$refs.assignLessonDialog.isOpen()
    },

    _onFilterChange(key, filterKey, value) {
      this.$emit(`update:${key}`, value)
      this.$emit('filter-update', {
        key: filterKey,
        value: value,
      })

      if (filterKey === 'lesson_type') {
        this.$refs.searchCourseRef.clearSearchText()
        this._searchCourse()
      }
    },
    _onTableOptionsChange(options) {
      this.$emit(`update:filterPage`, options.page)
      this.$emit(`update:filterLimit`, options.itemsPerPage)
      this.$emit('options-update', options)
    },
    _close(action) {
      if (action === null) {
        this.hide(true)
      } else {
        this.$emit('close', action)
      }
    },

    async _searchCourse(keyword = '') {
      this.courseSearching = true
      if (!keyword) {
        if (this.filter.lesson_type === 'weekday') {
          this.courseResultOptions = this.courseNameOptions.filter(el => el.lesson_type.includes('regular'))
        } else {
          this.courseResultOptions = this.courseNameOptions
        }
      } else {
        this.courseResultOptions = []
        try {
          let payload = {
            filter_item: [{ key: 'course_name', value: keyword }],
          }
          if (this.filterCenter > -1) {
            payload['center_id'] = this.filterCenter
          }
          const { data } = await this.$Fetcher.GetLessons(payload)
          for (let i = 0; i < data.length; i++) {
            const found = this.courseResultOptions.find(el => el.value === data[i].course_name)
            if (!found) {
              if (
                this.filter.lesson_type !== 'weekday' ||
                (this.filter.lesson_type === 'weekday' && data[i].lesson_type === 'regular')
              ) {
                this.courseResultOptions.push({
                  value: data[i].course_name,
                  text: data[i].course_name,
                  lesson_type: [data[i].lesson_type],
                })
              }
            } else {
              if (!found.lesson_type.includes(data[i].lesson_type)) {
                found.lesson_type.push(data[i].lesson_type)
              }
            }
          }
        } catch {}
      }

      this.courseSearching = false
    },
  },
}
</script>
